import * as React from 'react'

import { Box, Button, type SortDirection, Table, Text } from '@factorialco/deprecated-design-system'
import orderBy from 'lodash/orderBy'
import moment from 'moment'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import createComputed from 'modules/core/hooks/createComputed'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { matchText } from 'modules/core/lib/stringUtils'

import type { IApiKey } from 'modules/api_public/store/models/ApiKey'

import DeleteModal from '../Modals/DeleteModal'
import EditModal from '../Modals/EditModal'
import SearchBar from '../_SearchBar'
import { sortingApiKeys } from './Sorting'
import { Action, State } from './types'

interface Props {
  keys: IApiKey[]
}

const initialState = { editModal: false, deleteModal: false, key: null }
function reducer(_state: State, action: Action): State {
  switch (action.type) {
    case 'showEditModal':
      return { editModal: true, deleteModal: false, key: action.key }
    case 'showDeleteModal':
      return { editModal: false, deleteModal: true, key: action.key }
    case 'closeModals':
      return initialState
    default:
      throw new Error()
  }
}

const useFilter = createComputed((apiKeyList: IApiKey[], search: string) => {
  const searchapiKey = apiKeyList.filter((apiKeyList) => matchText(search, apiKeyList.name))

  return searchapiKey
})

const KeyTable = ({ keys }: Props) => {
  const [apiKeyId, setApiKeyId] = React.useState<number>()
  const [direction, setDirection] = React.useState<SortDirection>('asc')
  const [attribute, setAttribute] = React.useState<string>('name')
  const [state, dispatch] = React.useReducer(reducer, initialState)

  const onSort = (attribute: string, direction: SortDirection) => {
    setDirection(direction)
    setAttribute(attribute)
  }

  const sortedKeys = orderBy(keys, (key) => sortingApiKeys(key, attribute), direction)
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const TableOptions = () => <SearchBar search={search} onSearch={onSearch} />
  const filteredKeys = useFilter(sortedKeys, debouncedSearch).get()

  return (
    <>
      <Table autoLayout options={TableOptions}>
        <Table.Head>
          <Table.Row key="header">
            <Table.SortableHeadCell
              key="name"
              attribute="name"
              onSort={onSort}
              sortAttribute="name"
              sortDirection={direction}
              colSpan={4}
            >
              {i18n.t('settings.api_keys.key_name')}
            </Table.SortableHeadCell>
            <Table.SortableHeadCell
              key="created_at"
              attribute="created_at"
              onSort={onSort}
              sortAttribute="created_at"
              sortDirection={direction}
              colSpan={5}
            >
              {i18n.t('settings.api_keys.created_at')}
            </Table.SortableHeadCell>
            <Table.HeadCell colSpan={1} key="actions" />
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {filteredKeys.map((item) => (
            <Table.Row key={item.id}>
              <Table.Cell wide colSpan={4}>
                <Text.Normal weight="normal">{item.name}</Text.Normal>
              </Table.Cell>
              <Table.Cell wide colSpan={5}>
                <Text.Normal weight="normal">
                  {dateToString(moment(item.created_at), 'ui')}
                </Text.Normal>
              </Table.Cell>
              <Table.Cell verticalAlign="middle" colSpan={1}>
                <Box flexDirection="row" justifyContent="flexEnd" alignItems="center" gap="s8">
                  <PopoverMenu type="list" onClick={() => setApiKeyId(item.id)}>
                    <PopoverMenu.Trigger>
                      <Button.Decorative type="secondary" iconName="ellipsis" />
                    </PopoverMenu.Trigger>
                    <PopoverMenu.Content side="bottom" align="start">
                      <PopoverMenu.TouchableOption
                        title={i18n.t('settings.api_keys.rename_key')}
                        onClick={() => dispatch({ type: 'showEditModal', key: apiKeyId })}
                      />
                      <PopoverMenu.TouchableOption
                        danger
                        title={i18n.t('settings.api_keys.delete_key')}
                        onClick={() => dispatch({ type: 'showDeleteModal', key: apiKeyId })}
                      />
                    </PopoverMenu.Content>
                  </PopoverMenu>
                </Box>
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>

      {state.editModal && <EditModal dispatch={dispatch} state={state} />}
      {state.deleteModal && <DeleteModal dispatch={dispatch} state={state} />}
    </>
  )
}

export default observer(KeyTable)
